import axios from "axios";
import CryptoJS from "crypto-js";
import * as constants from "./constants";
import { incrementRequestCount, decrementRequestCount } from "./requestTracker";

const key = CryptoJS.enc.Hex.parse('f16c1ebc9e7401e60f127a0bdb27b3c8b67d35516ecf4786f37f2545e0031701');

// Special separator that's unlikely to appear in normal base64 strings
// Using multiple special characters to make it more unique
const IV_SEPARATOR = "::§§::";

const generateIV = () => {
    // Generate 16 random bytes
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return CryptoJS.lib.WordArray.create(array);
};

const apiInstance = axios.create({
    baseURL: `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/`,
});

// Request Interceptor
apiInstance.interceptors.request.use(
    (config) => {
        incrementRequestCount();
        if (!config.url.includes("login") && !config.url.includes("auth") && !config.url.includes("interview-link")) {
            const token = localStorage.getItem("authToken") || localStorage.getItem("candidateToken");
            if (token) {
                config.headers.Authorization = `Token ${token}`;

                if (config.data && constants.ENCRYPT_LOCAL_STORAGE) {
                    try {
                        // Generate a new IV for each request
                        const iv = generateIV();
                        const ivHex = CryptoJS.enc.Hex.stringify(iv);

                        if (config.data instanceof FormData) {
                            const jsonData = {};
                            for (let [key, value] of config.data.entries()) {
                                if (!(value instanceof File)) {
                                    jsonData[key] = value;
                                    config.data.delete(key);
                                }
                            }

                            if (Object.keys(jsonData).length > 0) {
                                const jsonString = JSON.stringify(jsonData);
                                const encryptedData = CryptoJS.AES.encrypt(
                                    jsonString,
                                    key,
                                    {
                                        iv: iv,
                                        mode: CryptoJS.mode.CBC,
                                        padding: CryptoJS.pad.Pkcs7
                                    }
                                ).toString();

                                // Combine IV and encrypted data
                                const payload = `${ivHex}${IV_SEPARATOR}${encryptedData}`;
                                config.data.append('encrypted_data', payload);
                            }

                            delete config.headers['Content-Type'];
                        } else {
                            const jsonString = JSON.stringify(config.data);
                            const encryptedData = CryptoJS.AES.encrypt(
                                jsonString,
                                key,
                                {
                                    iv: iv,
                                    mode: CryptoJS.mode.CBC,
                                    padding: CryptoJS.pad.Pkcs7
                                }
                            ).toString();

                            // Combine IV and encrypted data
                            const payload = `${ivHex}${IV_SEPARATOR}${encryptedData}`;
                            config.data = payload;
                            config.headers['Content-Type'] = 'text/plain';
                        }
                    } catch (e) {
                        console.error("Encryption error:", e);
                        return Promise.reject(e);
                    }
                }
                return config;
            } else {
                return Promise.reject("No token available");
            }
        }
        return config;
    },
    (error) => {
        decrementRequestCount();
        return Promise.reject(error);
    }
);

// Response Interceptor
apiInstance.interceptors.response.use(
    (response) => {
        if (response.data && !response?.request?.responseURL?.includes("export-excel") && typeof response.data === 'string' && constants.ENCRYPT_LOCAL_STORAGE) {
            try {
                // Split the response into IV and encrypted data
                const [ivHex, encryptedData] = response.data.split(IV_SEPARATOR);

                if (!ivHex || !encryptedData) {
                    throw new Error("Invalid encrypted response format");
                }

                const iv = CryptoJS.enc.Hex.parse(ivHex);
                const encryptedWordArray = CryptoJS.enc.Base64.parse(encryptedData);

                const decryptedData = CryptoJS.AES.decrypt(
                    { ciphertext: encryptedWordArray },
                    key,
                    {
                        iv: iv,
                        mode: CryptoJS.mode.CBC,
                        padding: CryptoJS.pad.Pkcs7,
                    }
                );

                const plaintext = decryptedData.toString(CryptoJS.enc.Utf8);

                try {
                    response.data = JSON.parse(plaintext);
                } catch (jsonError) {
                    console.warn("Decrypted response is not JSON. Returning plaintext as-is.");
                    decrementRequestCount();
                    response.data = plaintext;
                }
            } catch (e) {
                console.error("Decryption error:", e);
                return Promise.reject(new Error("Failed to decrypt response."));
            }
        }
        decrementRequestCount()
        return response;
    },
    (error) => {
        decrementRequestCount()
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        } else if (error.response && error.response.status === 401) {
            localStorage.removeItem("user");
            localStorage.removeItem("authToken");
            if (!window.location.href.includes("login")) {
                window.location.href = "/login";
            }
        }
        return Promise.reject(error);
    }
);

export default apiInstance;
export { apiInstance }; 